import {
    UntypedFormBuilder,
    UntypedFormGroup,
    ValidatorFn
} from '@angular/forms';
import {
    ClientPageSectionModel,
    PageSectionFieldValidator
} from '../../domain/models/dynamic-form-schema.model';
import { normalizeValidatorConfigs } from './dynamic-form-rules';
import { getExtraValidatorsForField } from './field-validator-rules';
import { atLeastOnePhoneFormValidator } from './form-validators';
import { createValidatorFromConfig } from './validator-registry';

export function buildDynamicForm(
    sections: ClientPageSectionModel[],
    fb: UntypedFormBuilder,
    initialValue?: Record<string, any>
): UntypedFormGroup {
    const formConfig: Record<string, [any, ValidatorFn[]]> = {};
    const sectionValidators: ValidatorFn[] = [];

    for (const section of sections) {
        const normalizedSectionValidators = normalizeValidatorConfigs(
            section.validators,
            { forceUniqueTargetKeys: true }
        );

        const needsPhoneFallbackValidator =
            section.rows.some((row) =>
                row.fields.some((field) =>
                    ['mobilePhone', 'homePhone', 'workPhone'].includes(
                        field.key
                    )
                )
            ) &&
            !normalizedSectionValidators.some(
                (validator) => validator.name === 'atLeastOnePhoneRequired'
            );

        if (needsPhoneFallbackValidator) {
            normalizedSectionValidators.push({
                name: 'atLeastOnePhoneRequired',
                message: 'shared.error.required.one-phone',
                errorKey: 'atLeastOnePhoneRequired'
            });
        }

        section.validators = normalizedSectionValidators;

        for (const row of section.rows) {
            for (const field of row.fields) {
                const baseValidators = normalizeValidatorConfigs(
                    field.validators as
                        | PageSectionFieldValidator[]
                        | Record<string, string>
                );
                const extraValidators = getExtraValidatorsForField(field);
                const allValidators = [...baseValidators, ...extraValidators];

                field.validators = allValidators;
                field.required = allValidators.some(
                    (validator) => validator.name === 'required'
                );

                // The signature "I approve" checkbox must be affirmatively checked (=== true).
                // Validators.required treats `false` as valid, so unchecking after checking would
                // still pass — map its `required` to `requiredTrue` (same `required` error key). (TC-21)
                const requiresAffirmative = field.type === 'signature';
                const validators: ValidatorFn[] = allValidators
                    .map((validator) =>
                        requiresAffirmative && validator.name === 'required'
                            ? createValidatorFromConfig({
                                  ...validator,
                                  name: 'requiredTrue'
                              })
                            : createValidatorFromConfig(validator)
                    )
                    .filter(
                        (fn): fn is ValidatorFn => typeof fn === 'function'
                    );

                formConfig[field.key] = [null, validators];
            }
        }

        sectionValidators.push(
            ...normalizedSectionValidators
                .map(createValidatorFromConfig)
                .filter((fn): fn is ValidatorFn => typeof fn === 'function')
        );
    }

    const form = fb.group(formConfig);
    form.addValidators(sectionValidators);

    setInitialValues(initialValue, form);

    return form;
}

function setInitialValues(
    initialValue: Record<string, any> | undefined,
    form: UntypedFormGroup
) {
    if (
        initialValue != null &&
        typeof initialValue === 'object' &&
        !Array.isArray(initialValue)
    ) {
        const safeValue: Record<string, any> = {};
        for (const key of Object.keys(initialValue)) {
            if (form.contains(key)) {
                safeValue[key] = initialValue[key];
            }
        }
        if (Object.keys(safeValue).length > 0) {
            form.patchValue(safeValue);
        }
    }
}
